import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Umbrella, Stethoscope, User as UserIcon, PartyPopper, HeartCrack, Scale } from "lucide-react";

/**
 * Employee-facing PTO balances card. Renders nothing when either:
 *   - The tenant has Benefits & PTO disabled globally
 *   - No benefit type is enabled for this employee
 *
 * Data source: GET /api/benefits/me (returns { enabled, hourly_rate, benefits: {...} })
 */

const META = {
  vacation:    { label: "Vacation",    Icon: Umbrella,    tone: "bg-sky-50 border-sky-200 text-sky-900" },
  sick:        { label: "Sick",        Icon: Stethoscope, tone: "bg-rose-50 border-rose-200 text-rose-900" },
  personal:    { label: "Personal",    Icon: UserIcon,    tone: "bg-violet-50 border-violet-200 text-violet-900" },
  holiday:     { label: "Holidays",    Icon: PartyPopper, tone: "bg-amber-50 border-amber-200 text-amber-900" },
  bereavement: { label: "Bereavement", Icon: HeartCrack,  tone: "bg-slate-50 border-slate-200 text-slate-900" },
  jury:        { label: "Jury",        Icon: Scale,       tone: "bg-emerald-50 border-emerald-200 text-emerald-900" },
};

export default function BenefitsBalancesCard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/benefits/me");
        setData(r.data);
      } catch {
        setData({ enabled: false, benefits: {} });
      }
    })();
  }, []);

  if (!data || !data.enabled) return null;
  const entries = Object.entries(data.benefits || {}).filter(([, cfg]) => cfg?.enabled);
  if (entries.length === 0) return null;

  return (
    <Card className="rounded-2xl border-slate-200" data-testid="employee-benefits-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Time-off balances</p>
            <p className="text-sm text-slate-600">Ask your admin to use any of these on your paystub.</p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {entries.map(([key, cfg]) => {
            const meta = META[key] || { label: key, Icon: Umbrella, tone: "bg-slate-50 border-slate-200 text-slate-900" };
            const Icon = meta.Icon;
            return (
              <div key={key} className={`rounded-xl border ${meta.tone} p-2.5`} data-testid={`benefit-balance-${key}`}>
                <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest opacity-70">
                  <Icon className="h-3 w-3" />
                  {meta.label}
                </div>
                <p className="text-xl font-extrabold font-display tabular-nums mt-0.5">
                  {Number(cfg.balance_hours || 0).toFixed(2)}h
                </p>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
